import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

interface InfiniteScrollProps {
  children: ReactNode;
  isLoading: boolean;
  onEnd?(): void;
}

export const InfiniteScroll = ({ children, isLoading, onEnd }: InfiniteScrollProps) => {
  const [isIntersecting, setIsIntersection] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const handleIntersection = (entries: IntersectionObserverEntry[]) =>
    entries.forEach((entry) => {
      setIsIntersection(entry.isIntersecting);
    });

  const observerRef = useRef(
    new IntersectionObserver(handleIntersection, {
      root: containerRef.current,
    })
  );

  useEffect(() => {
    if (isIntersecting && onEnd) onEnd();
  }, [isIntersecting]);

  useEffect(() => {
    if (!containerRef.current || !endRef.current) return;
    if (onEnd) {
      observerRef.current.observe(endRef.current);
    } else {
      observerRef.current.unobserve(endRef.current);
    }
    return () => observerRef.current.disconnect();
  }, [onEnd]);

  return (
    <Container ref={containerRef}>
      {children}
      {isLoading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}
      <div ref={endRef} />
    </Container>
  );
};

const Container = styled('div')({
  overflow: 'scroll',
  height: '100%',
});
